import 'chartjs-plugin-datalabels'; // Plugin for adding number overlays on the bar/pie slices
import 'chartjs-plugin-annotation';

import {
  FluidChartInterface,
  FluidFlexJustification,
  FluidTheme,
  FluidThemeInterface,
  isNullOrUndefined,
} from '@lmig/fluid-core';
import { generateUniqueId } from '@lmig/fluid-core/lib/helpers/string.helpers';
import {
  Component,
  ComponentInterface,
  Element,
  h,
  Prop,
  State,
  Watch,
} from '@stencil/core';
import { Chart, ChartSize } from 'chart.js'; // Charting library, uses HTML canvas
import { AnnotationOptions } from 'chartjs-plugin-annotation';

import { ChartSubtitle, ChartTitle } from '../../common/fluid-chart.components';
import { DARK_BLUE, WHITE } from '../../constants/fluid-chart.constants';
import { useI18n } from '../../providers/fluid-i18n-provider/adapter/fluid-i18n.adapter';
import { FluidI18nStore } from '../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import {
  ChartUtils,
  DataSet,
  DatasetOverrides,
  FluidChartStyles,
} from '../../utils/charts/fluid-chart-utils';
import { Utils } from '../../utils/utils';
import {
  ContainedChart,
  HeightContainedChart,
  ResponsiveChart,
} from './components/fluid-line-chart.components';

export interface FluidLineChartTranslationOptions {
  chartTitle?: string;
  subTitle?: string;
  label?: string;
  chartLabels?: string[];
  chartHoverData?: any[];
  legendLabels?: string[];
}

/**
 * @displayName Line Chart
 *
 * @contextData FLUID Line Chart is a data visualization component that displays a series of data points at regular intervals connected by continuous line(s). It is ideal for showing trends over time, displaying chronological data, or comparing multiple datasets.
 */
@Component({
  tag: 'fluid-line-chart',
  styleUrl: 'fluid-line-chart.scss',
  shadow: true,
})
export class FluidLineChart
  implements FluidThemeInterface, FluidChartInterface, ComponentInterface
{
  // ================================================================ //
  // -- Own Properties (Private)

  /**
   * Intrinsic/extrinsic width of the chart.
   */
  private _elementWidth: number;

  /**
   * Reference to the <canvas> element that is used to render the chart.
   */
  private _chartRef: HTMLCanvasElement;

  /**
   * Reference to the Chart.js chart instance.
   */
  private _chart: Chart;

  // ================================================================ //
  // -- Host Element

  /**
   * Reference to the host element.
   */
  @Element() host: HTMLFluidLineChartElement;

  // ================================================================ //
  // -- Internationalisation

  /* @internal */
  @Prop({ mutable: true }) fluidTranslate: FluidLineChartTranslationOptions;
  private _translateId: string = generateUniqueId();
  @State() private _i18nStore: FluidI18nStore | undefined;

  // ================================================================ //
  // -- Props

  /**
   * What theme settings should the component use?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * Make sure that the chart colors reflect the legend. For some reason,
   * the chart colors will set to LM and then the chart will switch to
   * legacy. This is a hacky solution to that issue, will update the
   * colors and re-render the chart when the legend is updated.
   */
  @Watch('theme')
  themeWatchHandler(newValue: string, oldValue: string) {
    if (newValue !== oldValue && this._chart && !this.colorOverride) {
      this._chart.data.datasets.forEach((set, index) => {
        const dsOverrides = this.generateDataSetOverrides();
        set.datalabels.backgroundColor = dsOverrides[index].backgroundColor;
        set.datalabels.color = ChartUtils.pickTextColor(
          dsOverrides[index].backgroundColor,
          WHITE,
          DARK_BLUE
        );
      });
      // Updates font sizes
      ChartUtils.updateChart(this._chart);
    }
  }

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * A unique identifier for your chart canvas element
   * Useful to specify for E2E, analytics, etc.
   * Defaults to a unique 36 char string
   */
  @Prop({ mutable: true }) chartId: string = generateUniqueId();

  /**
   * When provided, displays a heading title above the chart.
   */
  @Prop({ mutable: true }) chartTitle: string;

  /**
   * Supplementary subheading for the chart displayed below the "title".
   */
  @Prop({ mutable: true }) subTitle: string;

  /**
   * Takes an array of values for the bars in the chart
   * Each value is a number array, one per dataset
   */
  @Prop({ mutable: true }) chartData: number[][];

  @Watch('chartData')
  chartDataWatchHandler(newValue: number[][], oldValue: number[][]) {
    if (newValue !== oldValue) {
      let { colors } = FluidChartStyles[this.theme];

      if (this.colorOverride) {
        colors = ChartUtils.createColorsSequence(
          this.colorOverride,
          this.chartData as []
        );
      }

      ChartUtils.dataHandler(
        this._chart,
        undefined,
        newValue,
        colors,
        this.generateDataSetOverrides()
      );
    }
  }

  /**
   * Takes an array of string labels for the lines in the chart
   */
  @Prop({ mutable: true }) chartLabels: string[];

  @Watch('chartLabels')
  chartLabelsWatchHandler(newValue: string[], oldValue: string[]) {
    if (newValue !== oldValue) ChartUtils.labelHandler(this._chart, newValue);
  }

  /**
   * Takes in chart hover data to be rendered as a tooltip.
   * Each value is a object array, one per dataset
   */
  @Prop({ mutable: true }) chartHoverData: object[][];

  @Watch('chartHoverData')
  chartHoverDataWatchHandler(newValue: object[][], oldValue: object[][]) {
    if (newValue !== oldValue && this._chart)
      this._chart.options.tooltips.callbacks.afterBody = (tooltipItems) =>
        Object.entries(
          this.chartHoverData[tooltipItems[0].datasetIndex][
            tooltipItems[0].index
          ]
        )
          .map(([key, val]) => `${key}: ${val}`)
          .join('\n');
  }

  /**
   * Allows the user to pass in a unit of currency to prepend to to chart values
   * Defaults to an empty string if undefined, should you wish to not have currency in your units
   */
  @Prop({ mutable: true }) currencySymbol = '';

  /**
   * When provided, adds a white background with subtle drop shadow
   * to the chart's container.
   */
  @Prop({ mutable: true }) hasBackground = false;

  /**
   * A label to describe the data units in the chart (required).
   */
  @Prop({ mutable: true }) label = 'No label provided';

  /**
   * Allows user to feed in a double array to allow for a legend of different types
   * Example:
   * [["Flood, Hail, EM", "Low", "Moderate", "High", "Severe"],["Wind", "None", "Tier 2", "Tier 1", "Tier 1 - Florida"]]
   * This will produce a 2 level legend with 2 sets of corresponding categories, from left to right
   * The first legendLabels[n][0] will always be the name for that category
   */
  @Prop({ mutable: true }) legendLabels: string[][];

  /**
   * Lets the user decide if they want a legend
   */
  @Prop({ mutable: true }) hasLegend = true;

  /**
   * Allows the user to pass in a custom color scheme for the line graph.
   * If prop is not given the color scheme will default to styles in FluidChartStyles
   * When this is set the theme colors will be ignored
   */
  @Prop({ mutable: true }) colorOverride: string[];

  /**
   * Allows the user to pass dataset-specific overrides.
   * Includes 'fill' (boolean if the line should be filled) and 'dashed' (a list of numbers indicating length of lines and spaces in desired dash pattern).
   */
  @Prop({ mutable: true }) datasetOverrides: DatasetOverrides[];

  /**
   * Optional - Allows user to pass annotations for charts such as dotted lines.
   * See https://www.npmjs.com/package/chartjs-plugin-annotations for object info
   */
  @Prop({ mutable: true }) annotations: AnnotationOptions[];

  @Watch('annotations')
  annotationsWatchHandler(
    newValue: AnnotationOptions[],
    oldValue: AnnotationOptions[]
  ) {
    if (newValue !== oldValue)
      ChartUtils.annotationHandler(this._chart, newValue);
  }

  /**
   * Allows the user to toggle the lock on the aspect ratio of the chart.
   * Unlocking the aspect ratio will allow for the adjustment of width and height.
   * Defaults to true (aspect ratio locked).
   */
  @Prop({ mutable: true }) maintainAspectRatio = true;

  /**
   * Allows the user to set the height of the chart (in px). NOTE:
   * "maintainAspectRatio" Prop MUST be "false" for this Prop to
   * take effect.
   */
  @Prop({ mutable: true }) chartHeight: number;

  /**
   * Allows the user to set the width of the chart (in px). Adjusting
   * this value will override horizontal auto-resizing. NOTE: this Prop
   * REQUIRES "chartHeight" Prop to be provided & "maintainAspectRatio"
   * Prop MUST be "false".
   */
  @Prop({ mutable: true }) chartWidth: number;

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes
   */
  connectedCallback(): void {
    useI18n.bind(this)({
      init: () => this.setChartLabels(),
      language: () => this.setChartLabels(),
    });
    Utils.setGlobalTheme(this);
  }

  /**
   * Lifecycle Method to create the chart once the component loads
   *  data - takes in at least one dataset
   *  options - for styling and tweaking chart settings
   */
  componentDidLoad() {
    this.chartWidth
      ? (this._elementWidth = this.chartWidth)
      : (this._elementWidth = this.host.clientWidth);
    let { colors } = FluidChartStyles[this.theme];
    const { fonts } = FluidChartStyles[this.theme];

    if (this.colorOverride) {
      colors = ChartUtils.createColorsSequence(
        this.colorOverride,
        this.chartData as []
      );
    }

    const myChartRef = this._chartRef.getContext('2d');
    let datasets = undefined;
    if (this.chartData !== undefined) {
      datasets = ChartUtils.datasetInterfaceBuilder(
        this.chartData,
        colors,
        this.generateDataSetOverrides()
      );
    }

    this._chart = new Chart(myChartRef, {
      type: 'line',
      data: {
        labels:
          this._i18nStore?.translate(this.fluidTranslate?.chartLabels) ||
          this.chartLabels,
        datasets: datasets,
      },
      options: {
        plugins: {
          // Controls the datalabels plugin
          datalabels: {
            display: true,
            font: (context) => ({
              size: ChartUtils.fontLabelScaler(context.chart.width),
              weight: 'bold',
              family: fonts,
            }),
            formatter: (value: number): string =>
              ChartUtils.toTruncated(value, 1, this.currencySymbol),
            anchor: 'end', // Attaches them to the top of the line
            align: 'top', // Prevents them from sitting too low on the line
          },
        },
        annotation: this.annotations && {
          drawTime: 'afterDatasetsDraw',
          annotations: this.annotations,
        },
        layout: {
          padding: {
            left: 0,
            right: 30, // Needs to be a gap to display the last datalabels
            top: 40, // Needs to be a gap to display the datalabels on a line that reaches the top of the chart
            bottom: 0,
          },
        },
        responsive: true,
        maintainAspectRatio: this.maintainAspectRatio,
        aspectRatio: 4 / 3,
        scales: {
          xAxes: [
            {
              ticks: {
                fontFamily: fonts,
                fontSize: ChartUtils.getChartBodyFontSize(this._elementWidth),
              },
            },
          ],
          yAxes: [
            {
              ticks: {
                fontFamily: fonts,
                fontSize: ChartUtils.getChartBodyFontSize(this._elementWidth),
              },
            },
          ],
        },
        title: {
          display: false, // We are using a custom element
        },
        legend: {
          display: this.hasLegend,
          position: 'bottom',
          labels: {
            fontFamily: fonts,
            fontSize: ChartUtils.getChartBodyFontSize(this._elementWidth),
            usePointStyle: true, // Make the legend match the point color
          },
        },
        tooltips: {
          titleFontFamily: fonts,
          titleFontSize: ChartUtils.getChartBodyFontSize(this._elementWidth),
          bodyFontFamily: fonts,
          bodyFontSize: ChartUtils.getChartBodyFontSize(this._elementWidth),
          displayColors: false, // Deemed redundant and made the tooltip cramped
          callbacks: {
            title: () => {
              return null;
            },
            // Displays percentage on hover
            label: (tooltipItems, data) => {
              const value: any =
                data.datasets[tooltipItems.datasetIndex].data[
                  tooltipItems.index
                ];
              return (
                `${this.label}: ${
                  this.currencySymbol
                }${value.toLocaleString()}` ||
                `${this._i18nStore?.translate(this.fluidTranslate?.label)}: ${
                  this.currencySymbol
                }${value.toLocaleString()}`
              );
            },
            // Modifies hover tooltip element with anything hoverData passes, designed to handle unlimited entries
            afterBody: (tooltipItems) => {
              if (this.chartHoverData === undefined) {
                return '';
              } else {
                const translatedHoverData = this._i18nStore?.translate(
                  this.fluidTranslate?.chartHoverData
                );
                const index =
                  translatedHoverData?.[0] ||
                  this.chartHoverData[tooltipItems[0].datasetIndex][
                    tooltipItems[0].index
                  ];
                const result = Object.entries(index)
                  .map(([key, val]) => `${key}: ${val}`)
                  .join('\n');
                return result.trim();
              }
            },
          },
        },
        // @ts-ignore - This is a bug in the typings, the function signature is correct
        onResize: (chartInstance: Chart, size: ChartSize): void => {
          if (isNullOrUndefined(chartInstance)) return;
          this._elementWidth = size.width;
          ChartUtils.updateChart(chartInstance);
        },
      },
    });

    this._chart.render();
  }

  /**
   * Called every time the component is disconnected from the DOM.
   */
  disconnectedCallback() {
    this._i18nStore?.deregisterElement(this._translateId);
  }

  // ================================================================ //
  // -- Local Methods

  /**
   * Updates the chart labels whenever the language changes in the i18n provider.
   */
  setChartLabels() {
    if (this._chart) {
      this._chart.data.labels =
        this._i18nStore?.translate(this.fluidTranslate?.chartLabels) ||
        this.chartLabels;
      this._chart.data.datasets = this._chart.data.datasets.map(
        (ds: DataSet, i: number) => ({
          ...ds,
          ...this.generateDataSetOverrides()[i],
        })
      );
      this._chart.update();
    }
  }

  /**
   * Line Chart is a bit finicky when it comes to handling colors and such
   * - always use this to get the dataset overrides.
   */
  generateDataSetOverrides(): DatasetOverrides[] {
    // Create the overrides for each individual data set; if there are any being passed in then they will override the defaults.
    const dsOverrides: DatasetOverrides[] = [];
    let { colors } = FluidChartStyles[this.theme];
    if (this.colorOverride !== undefined) {
      colors = this.colorOverride;
    }
    for (let i = 0; i < this.chartData.length; i++) {
      const ds: DatasetOverrides = {};

      // Show Data Labels by default
      ds.showDataLabels = true;

      // Always set the background color to match the theme
      ds.backgroundColor = colors[i];
      // If this is a "filled" line then set the border to be a complimentary color
      if (
        this.datasetOverrides &&
        i < this.datasetOverrides.length &&
        this.datasetOverrides[i].fill
      ) {
        // Set the fill color to be the themed color, and the line to be complimentary
        ds.borderColor = ChartUtils.pickTextColor(colors[i], WHITE, DARK_BLUE);
      }

      // Grab the first element of the legend labels to use in the legend
      if (
        this.legendLabels &&
        this.legendLabels.length > i &&
        this.legendLabels[i].length > 0
      ) {
        const translatedLegend = this._i18nStore?.translate(
          this.fluidTranslate?.legendLabels
        );

        ds.label = this.legendLabels?.[i]?.[0] || translatedLegend?.[i]?.[0];
      }

      // Make sure that whatever the user is passing in will always "win"
      if (this.datasetOverrides && i < this.datasetOverrides.length) {
        dsOverrides.push({ ...ds, ...this.datasetOverrides[i] });
      } else {
        dsOverrides.push(ds);
      }
    }

    return dsOverrides;
  }

  /**
   * Used for calculating relative sizes
   * @return the chart element
   */
  getChart() {
    return this._chart;
  }

  /**
   * Used for calculating relative sizes
   * @return The width of the containing element
   */
  getElementWidth(): number {
    return this._elementWidth;
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    return (
      <div
        class={{
          [`fluid-line-chart theme-${this.theme}`]: true,
          'has-background': this.hasBackground,
        }}>
        <div class='container'>
          <fluid-grid
            justify={FluidFlexJustification.CENTER}
            removeSpacing={true}>
            <fluid-grid-item col={12}>
              <ChartTitle
                title={this.chartTitle}
                fluidTranslate={this.fluidTranslate}
                i18nStore={this._i18nStore}
                theme={this.theme}
              />
              <ChartSubtitle
                subTitle={this.subTitle}
                fluidTranslate={this.fluidTranslate}
                i18nStore={this._i18nStore}
                theme={this.theme}
              />
            </fluid-grid-item>
            {this.maintainAspectRatio && (
              <ResponsiveChart
                chartId={this.chartId}
                setRef={(el: HTMLCanvasElement) => (this._chartRef = el)}
              />
            )}
            {this.chartHeight &&
              !this.chartWidth &&
              !this.maintainAspectRatio && (
                <HeightContainedChart
                  chartId={this.chartId}
                  setRef={(el: HTMLCanvasElement) => (this._chartRef = el)}
                  chartHeight={this.chartHeight}
                />
              )}
            {this.chartHeight &&
              this.chartWidth &&
              !this.maintainAspectRatio && (
                <ContainedChart
                  chartHeight={this.chartHeight}
                  chartWidth={this.chartWidth}
                  chartId={this.chartId}
                  setRef={(el: HTMLCanvasElement) => (this._chartRef = el)}
                />
              )}
          </fluid-grid>
        </div>
      </div>
    );
  }
}
